import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FileText, Sparkles, TrendingUp } from "lucide-react";
import {
  type FactorHistoryPoint,
  generateMemo,
  getFactorHistory,
  getPriceTarget,
  getQuote,
  getScore,
  getScoreDecomposition,
  getScoreHistory,
  getSecurity,
  latestStance,
  patchFreshnessOverrides,
  patchValuationParams,
  type PriceTarget,
  type ScoreHistoryPoint,
  type SecurityDetail,
} from "@/lib/api";
import { fmtDate, fmtScore } from "@/lib/format";
import { Panel } from "@/components/ui/Panel";
import { Stat } from "@/components/ui/Stat";
import {
  ConfidenceBadge,
  RecommendationBadge,
  StanceBadge,
  ThesisBadge,
} from "@/components/ui/Badges";
import { Button } from "@/components/ui/Button";
import { Label, NumberInput } from "@/components/ui/Input";
import { DataTable } from "@/components/ui/Table";
import { LoadingState, EmptyState, ErrorState } from "@/components/ui/States";
import { DecompositionChart } from "@/components/charts/DecompositionChart";
import { SubScoreRadar } from "@/components/charts/SubScoreRadar";
import { MemoViewer } from "@/components/memo/MemoViewer";
import { FundamentalsPanel } from "@/components/panels/FundamentalsPanel";
import { BehavioralPanel } from "@/components/panels/BehavioralPanel";
import { PriceChart } from "@/components/charts/PriceChart";

export function CompanyPage() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  if (!Number.isFinite(id)) {
    return <ErrorState detail="Invalid security id" />;
  }
  return <CompanyView id={id} />;
}

function CompanyView({ id }: { id: number }) {
  const queryClient = useQueryClient();
  const security = useQuery({
    queryKey: ["security", id],
    queryFn: () => getSecurity(id),
  });
  const score = useQuery({
    queryKey: ["security", id, "score"],
    queryFn: () => getScore(id),
    retry: false,
  });
  const decomp = useQuery({
    queryKey: ["security", id, "decomposition"],
    queryFn: () => getScoreDecomposition(id),
    retry: false,
    enabled: score.isSuccess,
  });
  const history = useQuery({
    queryKey: ["security", id, "score-history"],
    queryFn: () => getScoreHistory(id, { limit: 30 }),
    retry: false,
  });
  const factorHistory = useQuery({
    queryKey: ["security", id, "factor-history"],
    queryFn: () => getFactorHistory(id, { days: 365 }),
    retry: false,
    enabled: score.isSuccess,
  });
  const factorHistoryByName = useMemo(() => {
    const map = new Map<string, FactorHistoryPoint[]>();
    for (const row of factorHistory.data ?? []) {
      const arr = map.get(row.factor_name) ?? [];
      arr.push(row);
      map.set(row.factor_name, arr);
    }
    return map;
  }, [factorHistory.data]);
  const stance = useQuery({
    queryKey: ["security", id, "stance"],
    queryFn: () => latestStance(id),
    retry: false,
  });
  const quote = useQuery({
    queryKey: ["security", id, "quote"],
    queryFn: () => getQuote(id),
    retry: false,
    // Refresh every 60 s — Yahoo Finance cache TTL
    refetchInterval: 60_000,
  });
  const priceTarget = useQuery({
    queryKey: ["security", id, "price-target"],
    queryFn: () => getPriceTarget(id),
    retry: false,
    // Params are set by the PATCH below; re-fetch after save
    enabled: !!(security.data?.valuation_params),
  });

  const generate = useMutation({
    mutationFn: () => generateMemo({ security_id: id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["memo", id] });
    },
  });

  if (security.isLoading) return <LoadingState message="Loading security…" />;
  if (security.isError || !security.data) return <ErrorState detail="Security not found." />;

  const sec = security.data;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <Link
            to="/screener"
            className="inline-flex items-center gap-1 text-2xs text-foreground-muted hover:text-foreground-secondary"
          >
            <ArrowLeft size={12} /> Back to screener
          </Link>
          <h1 className="text-xl font-semibold tracking-tight">
            {sec.ticker} <span className="text-foreground-secondary">— {sec.name}</span>
          </h1>
          <p className="text-2xs text-foreground-muted">
            {sec.exchange_mic} · {sec.currency} · {sec.security_type}
            {sec.asset_class ? ` · ${sec.asset_class}` : ""}
            {sec.country_code ? ` · ${sec.country_code}` : ""}
            {sec.cik ? ` · CIK ${sec.cik}` : ""}
          </p>
          {/* Live quote chip */}
          {quote.data && (
            <QuoteChip
              price={quote.data.price}
              change={quote.data.change}
              changePct={quote.data.change_pct}
              currency={quote.data.currency}
              marketOpen={quote.data.market_open}
            />
          )}
        </div>
        <Button
          variant="primary"
          size="sm"
          loading={generate.isPending}
          leadingIcon={<Sparkles size={12} />}
          onClick={() => generate.mutate()}
        >
          {generate.isPending ? "Generating…" : "Generate recommendation"}
        </Button>
      </header>

      <PriceChart securityId={id} ticker={sec.ticker} />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Panel>
          <Stat
            label="Composite"
            value={score.data ? fmtScore(score.data.composite) : "—"}
            hint={score.data ? `as of ${fmtDate(score.data.as_of)}` : "no score yet"}
          />
          {history.data && history.data.length >= 2 && (
            <div className="mt-2">
              <ScoreSparkline points={history.data} />
            </div>
          )}
        </Panel>
        <Panel>
          <Stat
            label="Confidence"
            value={
              score.data ? <ConfidenceBadge band={score.data.confidence_band} /> : "—"
            }
          />
        </Panel>
        <Panel>
          <Stat
            label="Thesis"
            value={
              score.data ? <ThesisBadge status={score.data.thesis_status} /> : "—"
            }
          />
        </Panel>
        <Panel>
          <Stat
            label="Stance"
            value={
              stance.data ? <StanceBadge label={stance.data.stance_label} /> : "—"
            }
            hint={stance.data ? `policy v${stance.data.policy_version_id}` : ""}
          />
          {stance.data && (
            <div className="mt-2 flex items-center gap-1.5">
              <span className="text-2xs text-foreground-muted">Recommendation:</span>
              <RecommendationBadge rec={stance.data.recommendation} />
            </div>
          )}
        </Panel>
      </div>

      {/* G7 — target price strip. Surfaced only when the policy produced
          a base-case target; mirrors the values shown lower in the stance
          explanation but pins them near the top for a glance read. */}
      {stance.data &&
        (stance.data.target_price !== null && stance.data.target_price !== undefined) && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Panel>
              <Stat
                label="Target price"
                value={`$${stance.data.target_price.toFixed(2)}`}
                hint={
                  stance.data.horizon_days != null
                    ? `over ~${stance.data.horizon_days}d`
                    : undefined
                }
              />
            </Panel>
            <Panel>
              <Stat
                label="Target return"
                value={
                  stance.data.target_return_pct != null
                    ? `${(stance.data.target_return_pct * 100).toFixed(1)}%`
                    : "—"
                }
                tone={
                  stance.data.target_return_pct != null
                    ? stance.data.target_return_pct > 0
                      ? "good"
                      : "bad"
                    : "default"
                }
                hint="vs last close"
              />
            </Panel>
            <Panel>
              <Stat
                label="Horizon"
                value={
                  stance.data.horizon_days != null
                    ? `${stance.data.horizon_days} days`
                    : "—"
                }
              />
            </Panel>
            <Panel>
              <Stat
                label="Kelly fraction"
                value={
                  stance.data.kelly_fraction != null
                    ? `${(stance.data.kelly_fraction * 100).toFixed(1)}%`
                    : "—"
                }
                hint="quarter-Kelly"
              />
            </Panel>
          </div>
        )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel
          className="lg:col-span-2"
          title="Score decomposition"
          description="Signed bps contribution per factor; positive = lifts the composite."
        >
          {!score.data && <EmptyState title="No score" description="No composite yet for this security." />}
          {decomp.isLoading && <LoadingState message="Loading decomposition…" />}
          {decomp.data && decomp.data.length > 0 && (
            <DecompositionChart rows={decomp.data} />
          )}
          {decomp.data && decomp.data.length === 0 && (
            <EmptyState title="No decomposition" />
          )}
        </Panel>

        <Panel title="Sub-scores">
          {score.data ? (
            <SubScoreRadar score={score.data} />
          ) : (
            <EmptyState title="No sub-scores" />
          )}
        </Panel>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel
          className="lg:col-span-2"
          title="Stance explanation"
          description="The rule that fired and the inputs it saw."
        >
          {stance.isLoading && <LoadingState />}
          {stance.isError && (
            <EmptyState
              title="No stance recorded"
              description="Run the policy engine after the score is published."
            />
          )}
          {stance.data && (
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-border-subtle bg-surface-base p-3">
                <p className="text-xs text-foreground-secondary">{stance.data.explanation}</p>
              </div>
              {/* G7 policy enrichment */}
              {(stance.data.target_price !== null && stance.data.target_price !== undefined) ||
              (stance.data.horizon_days !== null && stance.data.horizon_days !== undefined) ||
              (stance.data.target_return_pct !== null && stance.data.target_return_pct !== undefined) ||
              (stance.data.kelly_fraction !== null && stance.data.kelly_fraction !== undefined) ? (
                <div className="rounded-md border border-border-subtle bg-surface-subtle p-3 grid gap-2 sm:grid-cols-4 text-2xs">
                  {stance.data.target_price != null && (
                    <KV label="target price" value={`$${stance.data.target_price.toFixed(2)}`} />
                  )}
                  {stance.data.horizon_days != null && (
                    <KV label="horizon" value={`${stance.data.horizon_days}d`} />
                  )}
                  {stance.data.target_return_pct != null && (
                    <KV label="target return" value={`${(stance.data.target_return_pct * 100).toFixed(1)}%`} />
                  )}
                  {stance.data.kelly_fraction != null && (
                    <KV label="Kelly fraction" value={`${(stance.data.kelly_fraction * 100).toFixed(1)}%`} />
                  )}
                </div>
              ) : null}
              <dl className="grid gap-2 text-2xs sm:grid-cols-4">
                <KV label="firing rule" value={stance.data.firing_rule_id ?? "—"} />
                <KV label="policy version" value={stance.data.policy_version_id} />
                <KV label="inputs hash" value={<span className="font-mono">{stance.data.inputs_hash}</span>} />
                <KV label="as of" value={fmtDate(stance.data.as_of)} />
              </dl>
            </div>
          )}
        </Panel>

        <Panel title="Decomposition rows">
          {decomp.data && decomp.data.length > 0 ? (
            <DataTable
              dense
              rows={[...decomp.data].sort(
                (a, b) => Math.abs(b.contribution_bps) - Math.abs(a.contribution_bps),
              )}
              rowKey={(r) => r.factor_name}
              columns={[
                {
                  key: "name",
                  header: "Factor",
                  cell: (r) => (
                    <span className="font-mono text-2xs text-foreground-secondary">
                      {r.factor_name}
                    </span>
                  ),
                },
                {
                  key: "weight",
                  header: "Weight",
                  cell: (r) => r.weight.toFixed(3),
                  align: "right",
                },
                {
                  key: "z",
                  header: "z",
                  cell: (r) => (
                    <span className="tabular-nums">
                      {r.normalised_value.toFixed(2)}
                    </span>
                  ),
                  align: "right",
                },
                {
                  key: "bps",
                  header: "bps",
                  cell: (r) => (
                    <span
                      className={
                        r.contribution_bps >= 0 ? "text-success" : "text-danger"
                      }
                    >
                      {r.contribution_bps >= 0 ? "+" : ""}
                      {r.contribution_bps.toFixed(0)}
                    </span>
                  ),
                  align: "right",
                },
                {
                  key: "drift",
                  header: "12m drift",
                  align: "right",
                  cell: (r) => (
                    <FactorDriftSparkline
                      points={factorHistoryByName.get(r.factor_name) ?? []}
                    />
                  ),
                },
              ]}
            />
          ) : (
            <EmptyState title="No decomposition" />
          )}
        </Panel>
      </div>

      <FreshnessOverridesPanel securityId={id} sec={sec} />

      <ValuationParamsPanel
        securityId={id}
        sec={sec}
        priceTarget={priceTarget.data ?? null}
        livePrice={quote.data?.price ?? null}
        onSaved={() => {
          void queryClient.invalidateQueries({ queryKey: ["security", id] });
          void queryClient.invalidateQueries({ queryKey: ["security", id, "price-target"] });
        }}
      />

      <FundamentalsPanel securityId={id} />

      <BehavioralPanel securityId={id} />

      <Panel
        title={
          <span className="flex items-center gap-2">
            <FileText size={14} className="text-foreground-muted" />
            Research memo
          </span>
        }
        description="Auto-drafted, evidence-linked, every numeric claim cited."
        action={
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => window.open(`/api/v1/memos/${id}/markdown`, "_blank")}
            >
              Export Markdown
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => window.open(`/api/v1/memos/${id}/print.html`, "_blank")}
              title="Opens print-friendly HTML — use the browser's Print → PDF flow."
            >
              Print / PDF
            </Button>
          </div>
        }
      >
        <MemoViewer securityId={id} />
      </Panel>
    </div>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="uppercase tracking-wider text-foreground-muted">{label}</dt>
      <dd className="text-foreground-primary">{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Score sparkline — inline SVG, no extra dep
// ---------------------------------------------------------------------------

function ScoreSparkline({ points }: { points: ScoreHistoryPoint[] }) {
  if (points.length < 2) return null;

  const W = 88;
  const H = 28;
  const PADX = 2;
  const PADY = 3;

  const values = points.map((p) => p.composite);
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const range = maxV - minV || 1;

  const sx = (i: number) =>
    PADX + (i / (points.length - 1)) * (W - PADX * 2);
  const sy = (v: number) =>
    PADY + ((maxV - v) / range) * (H - PADY * 2);

  const ptStr = points.map((p, i) => `${sx(i)},${sy(p.composite)}`).join(" ");

  const first = points[0]!;
  const last = points[points.length - 1]!;
  const rising = last.composite >= first.composite;

  const areaD =
    `M${sx(0)},${sy(first.composite)} ` +
    points.map((p, i) => `L${sx(i)},${sy(p.composite)}`).join(" ") +
    ` L${sx(points.length - 1)},${H} L${sx(0)},${H} Z`;

  const delta = last.composite - first.composite;
  const tip = `${first.as_of} → ${last.as_of}: ${delta >= 0 ? "+" : ""}${delta.toFixed(1)}`;

  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      className="overflow-visible"
      role="img"
      aria-label={`Score trend: ${tip}`}
    >
      <title>{tip}</title>
      <path
        d={areaD}
        className={rising ? "fill-success/10" : "fill-danger/10"}
      />
      <polyline
        points={ptStr}
        fill="none"
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
        className={rising ? "stroke-success" : "stroke-danger"}
      />
      <circle
        cx={sx(points.length - 1)}
        cy={sy(last.composite)}
        r={2.5}
        className={rising ? "fill-success" : "fill-danger"}
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Per-factor drift sparkline — 12-month sector-z trend per row in the
// decomposition table. Surfaces signal drift where the operator makes
// decisions, instead of buried in the Governance page (BACKLOG C.6 / Tier 3.6).
// ---------------------------------------------------------------------------

function FactorDriftSparkline({ points }: { points: FactorHistoryPoint[] }) {
  // Drop NaN/null gaps — the sector-z column can be null for factors that
  // hit a coverage_flag on a given as_of.
  const filtered = points.filter(
    (p): p is FactorHistoryPoint & { sector_z_score: number } =>
      p.sector_z_score !== null && Number.isFinite(p.sector_z_score),
  );
  if (filtered.length < 2) {
    return (
      <span
        className="text-2xs text-foreground-muted"
        title="Insufficient history to plot drift"
      >
        —
      </span>
    );
  }

  const W = 64;
  const H = 18;
  const PADX = 2;
  const PADY = 2;

  const values = filtered.map((p) => p.sector_z_score);
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  // Force the y-axis to include 0 so the operator can see when the factor
  // crosses below/above the cross-section mean — that's the drift signal.
  const yMin = Math.min(minV, 0);
  const yMax = Math.max(maxV, 0);
  const range = yMax - yMin || 1;

  const sx = (i: number) =>
    PADX + (i / (filtered.length - 1)) * (W - PADX * 2);
  const sy = (v: number) =>
    PADY + ((yMax - v) / range) * (H - PADY * 2);

  const ptStr = filtered.map((p, i) => `${sx(i)},${sy(p.sector_z_score)}`).join(" ");
  const zeroY = sy(0);

  const last = filtered[filtered.length - 1]!;
  const first = filtered[0]!;
  const delta = last.sector_z_score - first.sector_z_score;
  // Drift colour: red when the factor's z has moved meaningfully against the
  // composite (downward for ↑-better factors); here we just use sign of delta.
  const rising = delta >= 0;
  const tip =
    `${first.as_of} → ${last.as_of}: ` +
    `z ${first.sector_z_score.toFixed(2)} → ${last.sector_z_score.toFixed(2)} ` +
    `(Δ ${delta >= 0 ? "+" : ""}${delta.toFixed(2)})`;

  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      className="inline-block overflow-visible align-middle"
      role="img"
      aria-label={`Factor drift: ${tip}`}
    >
      <title>{tip}</title>
      {/* Zero-line reference: dotted, low-contrast so it doesn't compete with
          the actual signal but marks the cross-section mean. */}
      <line
        x1={PADX}
        x2={W - PADX}
        y1={zeroY}
        y2={zeroY}
        strokeDasharray="1 2"
        strokeWidth={0.5}
        className="stroke-border-strong"
      />
      <polyline
        points={ptStr}
        fill="none"
        strokeWidth={1.25}
        strokeLinejoin="round"
        strokeLinecap="round"
        className={rising ? "stroke-success" : "stroke-danger"}
      />
      <circle
        cx={sx(filtered.length - 1)}
        cy={sy(last.sector_z_score)}
        r={1.75}
        className={rising ? "fill-success" : "fill-danger"}
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Live quote chip
// ---------------------------------------------------------------------------

function QuoteChip({
  price,
  change,
  changePct,
  currency,
  marketOpen,
}: {
  price: number;
  change: number;
  changePct: number;
  currency: string;
  marketOpen: boolean;
}) {
  const positive = change >= 0;
  const sign = positive ? "+" : "";
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-border-subtle bg-surface-subtle px-2.5 py-0.5 text-xs">
      <TrendingUp size={10} className="text-foreground-muted" />
      <span className="font-semibold tabular-nums">
        {currency} {price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
      <span className={positive ? "text-success" : "text-danger"}>
        {sign}{change.toFixed(2)} ({sign}{changePct.toFixed(2)}%)
      </span>
      {!marketOpen && (
        <span className="text-2xs text-foreground-muted">closed</span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Per-security freshness threshold overrides
// ---------------------------------------------------------------------------

const FRESHNESS_DOMAINS = [
  { key: "prices", label: "Prices", globalDefault: 5 },
  { key: "fundamentals", label: "Fundamentals", globalDefault: 120 },
  { key: "estimates", label: "Estimates", globalDefault: 30 },
  { key: "filings", label: "Filings", globalDefault: 200 },
] as const;

type FreshnessDomain = (typeof FRESHNESS_DOMAINS)[number]["key"];

function FreshnessOverridesPanel({
  securityId,
  sec,
}: {
  securityId: number;
  sec: SecurityDetail;
}) {
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<FreshnessDomain, string>>({
    prices: "",
    fundamentals: "",
    estimates: "",
    filings: "",
  });

  // Initialise from server data whenever the security record changes.
  useEffect(() => {
    const ov = sec.freshness_overrides ?? {};
    setValues({
      prices: ov["prices"] !== undefined ? String(ov["prices"]) : "",
      fundamentals: ov["fundamentals"] !== undefined ? String(ov["fundamentals"]) : "",
      estimates: ov["estimates"] !== undefined ? String(ov["estimates"]) : "",
      filings: ov["filings"] !== undefined ? String(ov["filings"]) : "",
    });
  }, [sec.freshness_overrides]);

  const save = useMutation({
    mutationFn: () => {
      const body: Partial<Record<FreshnessDomain, number | null>> = {};
      for (const { key } of FRESHNESS_DOMAINS) {
        const raw = values[key].trim();
        body[key] = raw === "" ? null : Number(raw);
      }
      return patchFreshnessOverrides(securityId, body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["security", securityId] }),
  });

  return (
    <Panel
      title="Freshness thresholds"
      description="Per-security overrides for stale-data detection. Leave blank to use the global default."
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {FRESHNESS_DOMAINS.map(({ key, label, globalDefault }) => (
          <div key={key} className="space-y-1">
            <Label htmlFor={`fo-${key}`}>
              {label}{" "}
              <span className="text-2xs text-foreground-muted">(default {globalDefault}d)</span>
            </Label>
            <NumberInput
              id={`fo-${key}`}
              value={values[key]}
              onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
              min={1}
              placeholder={String(globalDefault)}
              className="text-right"
            />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 pt-3">
        <Button
          variant="primary"
          size="sm"
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          Save overrides
        </Button>
        {save.isSuccess && (
          <span className="text-xs text-foreground-muted">Saved.</span>
        )}
        {save.isError && (
          <span className="text-xs text-danger">{(save.error as Error).message}</span>
        )}
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Valuation params + price target
// ---------------------------------------------------------------------------

function ValuationParamsPanel({
  securityId,
  sec,
  priceTarget,
  livePrice,
  onSaved,
}: {
  securityId: number;
  sec: SecurityDetail;
  priceTarget: PriceTarget | null;
  livePrice: number | null;
  onSaved: () => void;
}) {
  const stored = sec.valuation_params as
    | { method?: string; target_multiple?: number; notes?: string }
    | null
    | undefined;

  const [method, setMethod] = useState<string>(stored?.method ?? "pe");
  const [multiple, setMultiple] = useState<string>(
    stored?.target_multiple !== undefined ? String(stored.target_multiple) : "",
  );
  const [notes, setNotes] = useState<string>(stored?.notes ?? "");

  // Re-sync when the security record refreshes.
  useEffect(() => {
    const p = sec.valuation_params as
      | { method?: string; target_multiple?: number; notes?: string }
      | null
      | undefined;
    if (p) {
      setMethod(p.method ?? "pe");
      setMultiple(p.target_multiple !== undefined ? String(p.target_multiple) : "");
      setNotes(p.notes ?? "");
    }
  }, [sec.valuation_params]);

  const save = useMutation({
    mutationFn: () =>
      patchValuationParams(securityId, {
        method,
        target_multiple: Number(multiple) || 0,
        notes: notes.trim() || null,
      }),
    onSuccess: onSaved,
  });

  // Implied upside = (price_target - live_price) / live_price * 100
  const upside =
    priceTarget?.price_target != null && livePrice
      ? ((priceTarget.price_target - livePrice) / livePrice) * 100
      : null;

  return (
    <Panel
      title={
        <span className="flex items-center gap-2">
          <TrendingUp size={14} className="text-foreground-muted" />
          Valuation &amp; price target
        </span>
      }
      description="Set a target multiple; the platform computes the implied price using the latest annual figure."
    >
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Inputs */}
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="vp-method">Method</Label>
              <select
                id="vp-method"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="w-full rounded border border-border-subtle bg-surface-base px-2 py-1.5 text-sm text-foreground-primary focus:outline-none focus:ring-1 focus:ring-accent"
              >
                <option value="pe">P/E</option>
              </select>
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="vp-multiple">Target multiple (×)</Label>
              <NumberInput
                id="vp-multiple"
                value={multiple}
                onChange={(e) => setMultiple(e.target.value)}
                min={0}
                step={0.5}
                placeholder="e.g. 20"
                className="text-right"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="vp-notes">Notes (optional)</Label>
            <input
              id="vp-notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="5-yr sector avg, consensus, …"
              className="w-full rounded border border-border-subtle bg-surface-base px-2 py-1.5 text-sm text-foreground-primary placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              size="sm"
              loading={save.isPending}
              onClick={() => save.mutate()}
              disabled={!multiple || Number(multiple) <= 0}
            >
              Save
            </Button>
            {Number(multiple) <= 0 && multiple !== "" && (
              <span className="text-xs text-danger">Must be &gt; 0</span>
            )}
            {save.isSuccess && (
              <span className="text-xs text-foreground-muted">Saved.</span>
            )}
            {save.isError && (
              <span className="text-xs text-danger">{(save.error as Error).message}</span>
            )}
          </div>
        </div>

        {/* Price target readout */}
        <div className="flex flex-col gap-2 rounded-md border border-border-subtle bg-surface-subtle p-3">
          {priceTarget ? (
            <>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-semibold tabular-nums text-foreground-primary">
                  {priceTarget.price_target != null
                    ? priceTarget.price_target.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : "—"}
                </span>
                <span className="text-xs text-foreground-muted">price target</span>
              </div>
              <dl className="grid gap-1 text-2xs sm:grid-cols-2">
                <KV label="method" value={priceTarget.method.toUpperCase()} />
                <KV label="multiple" value={`${priceTarget.target_multiple}×`} />
                <KV
                  label={priceTarget.base_concept ?? "base"}
                  value={priceTarget.base_value?.toFixed(2) ?? "—"}
                />
                <KV
                  label="period end"
                  value={priceTarget.base_period_end ? fmtDate(priceTarget.base_period_end) : "—"}
                />
                {upside !== null && (
                  <KV
                    label="implied upside"
                    value={
                      <span className={upside >= 0 ? "text-success" : "text-danger"}>
                        {upside >= 0 ? "+" : ""}
                        {upside.toFixed(1)}%
                      </span>
                    }
                  />
                )}
                {priceTarget.notes && (
                  <div className="sm:col-span-2">
                    <KV label="notes" value={priceTarget.notes} />
                  </div>
                )}
              </dl>
            </>
          ) : (
            <EmptyState
              title="No price target"
              description={
                sec.valuation_params
                  ? "Save valid params to compute."
                  : "Set a target multiple and save."
              }
            />
          )}
        </div>
      </div>
    </Panel>
  );
}
